import { narrowType } from '../types/assertType';
import { isDefined } from '../types/isDefined';

/**
 * Template-literal-like string template intended to replace ${prop.path} with property from given object.
 */
export type StringTemplate = string;

const areAllValuesEmpty = (
  template: string,
  record: Record<string, unknown>,
) => {
  const keys = [...template.matchAll(/\${([^}]+)}/g)].map((m) =>
    narrowType(isDefined, m[1]),
  );

  return (
    keys.filter(
      (k) =>
        getValueFromRecord(k, record) !== null &&
        getValueFromRecord(k, record) !== undefined,
    ).length === 0 && keys.length !== 0
  );
};

const getValueFromRecord = (
  propPath: string,
  record: Record<string, unknown>,
) =>
  propPath
    .split('.')
    .reduce((subrecord: any, propName) => subrecord?.[propName], record);

const getValueFromFallback = (
  propPath: string,
  fallback?: string | Record<string, unknown>,
) => {
  if (typeof fallback === 'string') {
    return fallback;
  }
  if (typeof fallback === 'object') {
    return getValueFromRecord(propPath, fallback) ?? '';
  }
  return '';
};

/**
 * Replace sequences of ${path} by corresponding items from record
 *
 * For example, ${foo.bar} is replaced by record.foo.bar.
 * Fallback parameter can be used to replace missing parameters instead.
 * If a string is provided, all paths missing in the record are replaced by it.
 * If an object is provided, it is used instead of `record` for missing paths.
 *
 * @example
 * formatStringTemplate(
 *   'Welcome ${employee.name} ${employee.middle} ${employee.surname} in ${company}',
 *   {employee: {name: 'John'}},
 *   {employee: {name: 'Jack', surname: 'Great'}, company: 'IP Fabric'}
 * ) // -> 'Welcome John  Great in IP Fabric'
 */
export const formatStringTemplate = (
  template: StringTemplate | StringTemplate[],
  record: Record<string, unknown>,
  fallback?: string | Record<string, unknown>,
  delimiter?: string,
): string => {
  if (Array.isArray(template)) {
    return template
      .map((t) => formatStringTemplate(t, record, fallback))
      .filter((s) => s !== '')
      .join(delimiter ?? ' ');
  }

  return areAllValuesEmpty(template, record)
    ? ''
    : template.replace(
        /\${([^}]+)}/g,
        (_str, propPath: string) =>
          getValueFromRecord(propPath, record) ??
          getValueFromFallback(propPath, fallback),
      );
};
